import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../../contexts/ThemeContext';
import WidgetShell from './WidgetShell';
import type { StatCounterItem } from './types';

export interface StatCounterGridProps {
  title: string;
  counters: StatCounterItem[];
  isLoading?: boolean;
  embedded?: boolean;
  columnsClassName?: string;
  showTotal?: boolean;
  viewAllPath?: string;
}

const StatCounterGrid: React.FC<StatCounterGridProps> = ({
  title,
  counters,
  isLoading,
  embedded,
  columnsClassName,
  showTotal = true,
  viewAllPath,
}) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const total = counters.reduce((sum, item) => sum + item.value, 0);
  const cols =
    columnsClassName ??
    (counters.length <= 3
      ? 'grid-cols-1 sm:grid-cols-3'
      : counters.length === 4
        ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'
        : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-5');

  return (
    <WidgetShell
      title={title}
      embedded={embedded}
      viewAllPath={viewAllPath}
      isLoading={isLoading}
      headerExtra={
        !isLoading && showTotal ? (
          <div className={`rounded-full border px-3 py-1 text-xs font-medium ${colors.border} ${colors.textMuted}`}>
            Total: <span className={colors.text}>{total}</span>
          </div>
        ) : null
      }
      skeleton={
        <div className={`grid ${cols} gap-2.5`}>
          {Array.from({ length: Math.max(counters.length, 3) }).map((_, idx) => (
            <div key={idx} className={`h-24 rounded-xl ${colors.surfaceHover} animate-pulse`} />
          ))}
        </div>
      }
    >
      <div className={`grid ${cols} gap-2.5`}>
        {counters.map((item) => (
          <button
            type="button"
            key={item.key}
            onClick={() => navigate(item.href)}
            className={`group rounded-xl border p-3 text-left transition-all ${colors.border} ${colors.interactiveHover} hover:-translate-y-[1px]`}
          >
            <div className="flex items-center justify-between">
              <span className={`text-xs ${colors.textMuted}`}>{item.label}</span>
              {item.icon ? (
                <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-white ${item.accent}`}>
                  {item.icon}
                </span>
              ) : (
                <span className={`inline-flex h-2 w-2 rounded-full ${item.accent}`} />
              )}
            </div>
            <div className={`mt-2 text-2xl font-semibold tracking-tight ${colors.text}`}>{item.value}</div>
            <div className={`mt-2 h-1.5 w-full rounded-full ${colors.surfaceHover}`}>
              <div
                className={`h-1.5 rounded-full ${item.accent}`}
                style={{ width: `${Math.min(100, total > 0 ? (item.value / total) * 100 : 0)}%` }}
              />
            </div>
          </button>
        ))}
      </div>
    </WidgetShell>
  );
};

export default StatCounterGrid;
